
import { useCallback, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Paintbrush,
  House,
  Building2,
  Droplets,
  ShieldCheck,
} from "lucide-react";

import Image1 from "../../assests/team/herosection/cubehero1.webp";
import Image2 from "../../assests/team/herosection/cubehero2.webp";
import Image3 from "../../assests/team/herosection/cubehero3.webp";
import Image4 from "../../assests/team/herosection/cubehero4.webp";

const slides = [
  {
    id: 1,
    number: "01",
    tag: "INTERIORS · CONSTRUCTION · PAINTING",
    title: "From Thoughtful",
    highlight: "Designs to Perfect Finishes.",
    desc: "Transform your space with Cube4Spaces and Paint Studio. From bespoke interior design and turnkey construction to professional interior and exterior painting, we bring design, quality and craftsmanship together to create spaces that feel exceptional.",
    image: Image1,
    imageAlt: "Premium interior design and architectural finishes",
  },
  {
    id: 2,
    number: "02",
    tag: "INTERIOR DESIGN",
    title: "Spaces Designed",
    highlight: "Around Your Lifestyle.",
    desc: "Discover bespoke interior design, thoughtful space planning, refined materials and turnkey interior solutions for residential and commercial spaces.",
    image: Image2,
    imageAlt: "Modern residential and commercial interior design",
  },
  {
    id: 3,
    number: "03",
    tag: "CONSTRUCTION",
    title: "Built with Quality.",
    highlight: "Delivered with Confidence.",
    desc: "From residential projects to commercial developments, we combine careful planning, construction expertise and coordinated execution.",
    image: Image3,
    imageAlt: "Residential and commercial construction",
  },
  {
    id: 4,
    number: "04",
    tag: "PAINTING · WATERPROOFING",
    title: "Beautiful Finishes.",
    highlight: "Lasting Protection.",
    desc: "Enhance and protect your property with professional interior and exterior painting, waterproofing and decorative finishes from Paint Studio.",
    image: Image4,
    imageAlt: "Professional painting and decorative wall finishes",
  },
];

const services = [
  {
    icon: House,
    title: "Interior Design",
    description: "Interior design & turnkey solutions",
  },
  {
    icon: Building2,
    title: "Construction",
    description: "Residential & commercial construction",
  },
  {
    icon: Paintbrush,
    title: "Painting",
    description: "Interior & exterior painting",
  },
  {
    icon: Droplets,
    title: "Finishes & Protection",
    description: "Waterproofing & decorative finishes",
  },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const activeSlide = slides[current];

  const nextSlide = useCallback(() => {
    setCurrent((previous) => (previous + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrent(
      (previous) => (previous - 1 + slides.length) % slides.length
    );
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrent(index);
  }, []);

  useEffect(() => {
    slides.forEach((slide) => {
      const image = new Image();
      image.src = slide.image;
    });
  }, []);

  useEffect(() => {
    if (paused) return undefined;

    const timer = window.setInterval(nextSlide, 6500);

    return () => window.clearInterval(timer);
  }, [nextSlide, paused]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <section
      aria-label="Cube4Spaces interior design, construction and Paint Studio services"
      className="relative isolate mt-16 flex min-h-[760px] min-h-[88svh] w-full flex-col overflow-hidden bg-neutral-950 font-['DM_Sans',sans-serif] text-white lg:min-h-[820px] xl:min-h-[860px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setPaused(false);
        }
      }}
    >
      {/* Background image slider */}
      <div className="absolute inset-0 -z-20 bg-neutral-900">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              current === index
                ? "z-10 opacity-100"
                : "pointer-events-none z-0 opacity-0"
            }`}
            aria-hidden="true"
          >
            <img
              src={slide.image}
              alt=""
              className="h-full w-full object-cover object-center"
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "auto"}
              decoding="async"
            />
          </div>
        ))}
      </div>

      {/* Premium dark overlays */}
      <div className="absolute inset-0 -z-10 bg-black/25" />

      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/90 via-black/70 to-black/25" />

      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/95 via-black/20 to-black/20" />

      {/* Ambient lighting */}
      <div className="pointer-events-none absolute -left-32 top-[10%] -z-10 size-80 rounded-full bg-amber-100/10 blur-[120px]" />

      <div className="pointer-events-none absolute -bottom-32 -right-20 -z-10 size-96 rounded-full bg-amber-200/[0.08] blur-[130px]" />

      {/* Centered content container */}
      <div className="mx-auto flex w-full max-w-[1440px] flex-1 items-center px-5 pb-40 pt-20 sm:px-8 sm:pb-40 md:px-10 lg:px-12 lg:pb-36 lg:pt-24 xl:px-16 2xl:px-20">
        <div
          key={activeSlide.id}
          className="grid w-full grid-cols-1 items-center gap-10 motion-safe:animate-[heroEnter_650ms_ease-out] lg:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)] lg:gap-12 xl:gap-16"
        >
          {/* Main hero content */}
          <div className="w-full max-w-[850px]">
            {/* Eyebrow */}
            <div className="mb-6 inline-flex max-w-full items-center gap-3 rounded-full border border-white/20 bg-white/[0.08] px-4 py-2.5 backdrop-blur-xl sm:mb-8 sm:px-5">
              <span className="size-2 shrink-0 rounded-full bg-amber-100 shadow-[0_0_12px_rgba(255,255,255,0.5)]" />

              <span className="font-['Poppins',sans-serif] text-[9px] font-medium uppercase tracking-[0.13em] text-white/90 sm:text-[10px] sm:tracking-[0.17em]">
                {activeSlide.tag}
              </span>
            </div>

            {/* SEO-friendly main heading */}
            <h1 className="flex flex-col gap-2 break-words font-['Poppins',sans-serif] text-[clamp(2.25rem,5.4vw,5.5rem)] font-semibold leading-[1.17] tracking-[-0.055em] text-white">
              <span>{activeSlide.title}</span>

              <span className="font-normal text-amber-100">
                {activeSlide.highlight}
              </span>
            </h1>

            {/* Accent divider */}
            <div className="mt-6 flex items-center gap-1.5 sm:mt-8">
              <span className="h-0.5 w-11 bg-amber-100" />
              <span className="h-0.5 w-3 bg-white/50" />
            </div>

            {/* Hero description */}
            <p className="mt-5 max-w-[680px] text-sm leading-7 text-white/80 sm:mt-6 sm:text-base sm:leading-8">
              {activeSlide.desc}
            </p>

            {/* Brand identities */}
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3">
              <div className="rounded border border-white/20 bg-white/[0.07] px-4 py-3 backdrop-blur-md">
                <p className="font-['Poppins',sans-serif] text-sm font-semibold tracking-wide text-white sm:text-base">
                  Cube4Spaces
                </p>
                <p className="mt-1 text-[10px] tracking-wide text-white/60 sm:text-xs">
                  Design & Build
                </p>
              </div>

              <span className="font-['Poppins',sans-serif] text-lg font-light text-amber-100">
                ×
              </span>

              <div className="rounded border border-white/20 bg-white/[0.07] px-4 py-3 backdrop-blur-md">
                <p className="font-['Poppins',sans-serif] text-sm font-semibold tracking-wide text-white sm:text-base">
                  Paint Studio
                </p>
                <p className="mt-1 text-[10px] tracking-wide text-white/60 sm:text-xs">
                  Paint & Protect
                </p>
              </div>
            </div>

            {/* Primary and secondary CTAs */}
            <div className="mt-7 flex flex-wrap items-center gap-3 sm:mt-9 sm:gap-4">
              <button
                type="button"
                onClick={() => scrollToSection("services")}
                className="group inline-flex min-h-12 items-center justify-between gap-3 rounded-md bg-white py-2 pl-5 pr-2.5 font-['Poppins',sans-serif] text-[11px] font-semibold text-neutral-950 shadow-xl transition duration-300 hover:-translate-y-0.5 hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber-100 sm:min-h-14 sm:gap-4 sm:pl-6 sm:text-xs"
              >
                <span>Explore Our Services</span>

                <span className="grid size-8 shrink-0 place-items-center rounded bg-neutral-950 text-white transition-transform duration-300 group-hover:rotate-45 sm:size-9">
                  <ArrowUpRight size={18} strokeWidth={1.8} />
                </span>
              </button>

              <button
                type="button"
                onClick={() => scrollToSection("contact")}
                className="group inline-flex min-h-12 items-center gap-3 rounded-md border border-white/30 bg-white/[0.06] px-4 font-['Poppins',sans-serif] text-[11px] font-medium text-white backdrop-blur-xl transition duration-300 hover:-translate-y-0.5 hover:border-amber-100/70 hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber-100 sm:min-h-14 sm:gap-4 sm:px-5 sm:text-xs"
              >
                <span>Get a Free Consultation</span>
                <ArrowUpRight
                  size={17}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>
            </div>

            {/* Brand positioning line */}
            <p className="mt-6 max-w-[650px] text-xs leading-6 text-white/70 sm:mt-8 sm:text-sm">
              <span className="font-semibold text-white">
                Cube4Spaces × Paint Studio
              </span>
              <span className="text-amber-100">
                {" "}— Designed to Inspire. Finished to Perfection.
              </span>
            </p>
          </div>

          {/* Desktop service panel */}
          <aside className="hidden w-full max-w-[365px] justify-self-end rounded-md border border-white/20 bg-black/25 p-5 shadow-2xl backdrop-blur-xl lg:block xl:p-6">
            <div className="flex items-center justify-between gap-3">
              <span className="font-['Poppins',sans-serif] text-[10px] font-medium tracking-[0.15em] text-white/85">
                OUR SERVICES
              </span>

              <span className="font-['Poppins',sans-serif] text-[10px] text-amber-100">
                04 SPECIALTIES
              </span>
            </div>

            <div className="my-5 h-px bg-white/20" />

            <div className="space-y-1">
              {services.map((service, index) => {
                const Icon = service.icon;

                return (
                  <div
                    key={service.title}
                    className="group flex items-start gap-3 rounded-md px-2 py-4 transition-colors duration-200 hover:bg-white/[0.07]"
                  >
                    <span className="grid size-10 shrink-0 place-items-center rounded border border-white/15 bg-white/[0.05] text-amber-100 transition-colors group-hover:border-amber-100/40">
                      <Icon size={19} strokeWidth={1.6} />
                    </span>

                    <div className="min-w-0 flex-1 pt-0.5">
                      <h2 className="font-['Poppins',sans-serif] text-xs font-medium text-white">
                        {service.title}
                      </h2>

                      <p className="mt-1 text-[11px] leading-5 text-white/60">
                        {service.description}
                      </p>
                    </div>

                    <span className="pt-1 font-['Poppins',sans-serif] text-[10px] text-white/40">
                      0{index + 1}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 border-t border-white/20 pt-5">
              <div className="flex items-center gap-2">
                <ShieldCheck
                  size={17}
                  strokeWidth={1.6}
                  className="shrink-0 text-amber-100"
                />

                <p className="font-['Poppins',sans-serif] text-[10px] font-medium text-white/80">
                  Design. Build. Paint. Protect.
                </p>
              </div>

              <p className="mt-2 pl-6 text-[11px] leading-5 text-white/55">
                One coordinated approach to creating and finishing exceptional spaces.
              </p>
            </div>
          </aside>
        </div>
      </div>

      {/* Bottom slider navigation */}
      <div className="absolute inset-x-0 bottom-0 z-20 border-t border-white/15 bg-black/25 backdrop-blur-md">
        <div className="mx-auto grid min-h-[72px] w-full max-w-[1440px] grid-cols-[1fr_auto] items-center gap-3 px-5 sm:min-h-[82px] sm:px-8 md:px-10 lg:grid-cols-[1fr_auto_1fr] lg:px-12 xl:px-16 2xl:px-20">
          {/* Slide counter */}
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="font-['Poppins',sans-serif] text-xs font-medium text-amber-100 sm:text-sm">
              {activeSlide.number}
            </span>

            <span className="h-px w-5 bg-white/50 sm:w-8" />

            <span className="font-['Poppins',sans-serif] text-[10px] text-white/55 sm:text-xs">
              04
            </span>

            <span className="ml-1 hidden font-['Poppins',sans-serif] text-[9px] tracking-[0.13em] text-white/50 xl:block">
              FEATURED EXPERIENCE
            </span>
          </div>

          {/* Carousel controls */}
          <div className="flex items-center justify-end gap-1.5 sm:gap-2">
            <div
              className="mr-1 flex items-center gap-1.5 sm:mr-3 sm:gap-2"
              role="group"
              aria-label="Choose featured slide"
            >
              {slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-pressed={current === index}
                  className={`h-1.5 rounded-full transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber-100 ${
                    current === index
                      ? "w-6 bg-amber-100 sm:w-7"
                      : "w-1.5 bg-white/40 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={prevSlide}
              aria-label="Previous slide"
              className="grid size-8 place-items-center rounded border border-white/25 bg-white/5 text-white transition hover:border-amber-100 hover:bg-amber-100 hover:text-neutral-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-100 sm:size-10"
            >
              <ChevronLeft size={18} strokeWidth={1.7} />
            </button>

            <button
              type="button"
              onClick={nextSlide}
              aria-label="Next slide"
              className="grid size-8 place-items-center rounded border border-white/25 bg-white/5 text-white transition hover:border-amber-100 hover:bg-amber-100 hover:text-neutral-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-100 sm:size-10"
            >
              <ChevronRight size={18} strokeWidth={1.7} />
            </button>
          </div>

          {/* Desktop scroll link */}
          <button
            type="button"
            onClick={() => scrollToSection("services")}
            className="group hidden items-center justify-self-end gap-3 font-['Poppins',sans-serif] text-[9px] font-medium tracking-[0.14em] text-white/60 transition hover:text-white lg:flex"
          >
            <span>EXPLORE SERVICES</span>

            <span className="h-px w-8 bg-white/50 transition-all group-hover:w-11 group-hover:bg-amber-100" />
          </button>
        </div>
      </div>

      {/* Entrance animation */}
      <style>{`
        @keyframes heroEnter {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            scroll-behavior: auto !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </section>
  );
}
